import { Lock } from 'lucide-react'
import IdentidadFiscal from '@/components/cd2b/IdentidadFiscal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { estiloBandera } from '@/lib/pais'

/**
 * Paso 1 — Cliente y país.
 *
 * El PAÍS va primero y no por estética: de él dependen el catálogo contable, la facturación, el
 * impuesto del resumen y la política con la que se calcula la comisión. Elegirlo al final dejaba
 * al asesor armando un paquete con números que cambiaban al tocar el último desplegable.
 */
export default function PasoCliente({ c }) {
  const { form, setForm, esNueva, propuesta, paises, paisesError, paisBloqueado, catalogo, giroSel } = c

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-h3">Cliente y empresa</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {/* Las addendas cambian el paquete de una empresa que YA existe: no llevan país. */}
          {propuesta?.tipo !== 'cambio' && (
            <div className="sm:col-span-2">
              <Label className="flex items-center gap-1.5">
                País de la empresa
                {paisBloqueado && <Lock size={12} className="text-subtle" aria-label="Bloqueado" />}
              </Label>
              <Select value={form.pais || undefined} disabled={paisBloqueado}
                onValueChange={(v) => setForm((f) => ({ ...f, pais: v }))}>
                <SelectTrigger className="sm:max-w-sm">
                  <SelectValue placeholder={paises === null ? 'Cargando países…' : 'Elige el país…'} />
                </SelectTrigger>
                <SelectContent>
                  {(paises || []).map((x) => (
                    <SelectItem key={x.code} value={x.code}>
                      <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true" className="inline-block h-3 w-4 rounded-sm border border-line" style={estiloBandera(x.code)} />
                        {x.nombre} ({x.code})
                      </span>
                    </SelectItem>
                  ))}
                  {/* País guardado que ya no está en la lista (p. ej. pasó a hoja de ruta): se muestra, no se pierde. */}
                  {form.pais && !(paises || []).some((x) => x.code === form.pais) && (
                    <SelectItem value={form.pais}>{form.pais}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {paisBloqueado ? (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                  La empresa ya está creada: el país no se puede cambiar (su catálogo contable y su
                  facturación dependen de él).
                </p>
              ) : c.esPagada ? (
                <p className="text-caption text-warning-700 dark:text-warning-300 mt-1">
                  Esta cotización se pagó sin país. Elígelo y guarda para poder entregarla: después ya no
                  se puede cambiar.
                </p>
              ) : (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                  Obligatorio para crear la empresa. No se puede cambiar después.
                </p>
              )}
              {paisesError && (
                <Alert variant="warning" className="mt-2">
                  <AlertDescription>
                    No se pudo cargar la lista de países. Recarga la página; sin ella no se puede elegir país.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="cot-correo">Correo del cliente</Label>
            <Input id="cot-correo" type="email" value={form.clienteEmail} disabled={!esNueva}
              onChange={(e) => setForm({ ...form, clienteEmail: e.target.value })} />
            {esNueva && <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">Si no existe, se crea al activar.</p>}
          </div>
          <div>
            <Label htmlFor="cot-empresa">Nombre de la empresa</Label>
            <Input id="cot-empresa" value={form.empresaNombre}
              onChange={(e) => setForm({ ...form, empresaNombre: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label>Giro del negocio</Label>
            <Select value={form.giro || undefined} onValueChange={c.elegirGiro}>
              <SelectTrigger><SelectValue placeholder="Elige el giro…" /></SelectTrigger>
              <SelectContent>
                {(catalogo.giros || []).map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {giroSel?.desc && <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">{giroSel.desc}</p>}
            {/* El giro guardado ya no está publicado: no se rompe la cotización, pero el
                aprovisionamiento la rechazaría. Se avisa en vez de fallar en silencio. */}
            {form.giro && !giroSel && (
              <Alert variant="warning" className="mt-2">
                <AlertDescription>
                  El giro <code className="font-mono">{form.giro}</code> ya no está publicado en el
                  catálogo. Elige uno de la lista antes de crear la empresa.
                </AlertDescription>
              </Alert>
            )}
            {/* La plantilla del giro trae una edición sugerida; se OFRECE, no se impone. */}
            {giroSel?.edicion && giroSel.edicion !== form.edicion && (
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-2">
                Este giro suele contratarse en{' '}
                <strong className="text-neutral-700 dark:text-neutral-300">{c.edicionLabel(giroSel.edicion)}</strong>.{' '}
                <button type="button" className="text-primary font-medium underline"
                  onClick={() => c.cambiarEdicion(giroSel.edicion)}>
                  Aplicar
                </button>
              </p>
            )}
            {/* Lo que el rubro suele llevar. Se OFRECE, no se impone: el paquete es del asesor. */}
            {c.sugeridosDelGiro && (c.sugeridosDelGiro.modulos.length > 0 || c.sugeridosDelGiro.app) && (
              <div className="mt-3 rounded-md border border-line p-3 space-y-2">
                <p className="text-body-sm font-medium text-neutral-800 dark:text-neutral-200">
                  Para este rubro solemos recomendar:{' '}
                  <span className="font-normal text-neutral-700 dark:text-neutral-300">
                    {[...c.sugeridosDelGiro.modulos.map((m) => m.label), ...(c.sugeridosDelGiro.app ? [c.sugeridosDelGiro.app.label] : [])].join(', ')}
                  </span>
                </p>
                {c.sugeridosDelGiro.faltanModulos.length > 0 || c.sugeridosDelGiro.faltaApp || c.sugeridosDelGiro.faltaAsientos ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-caption text-neutral-600 dark:text-neutral-400">
                      Faltan en el paquete:{' '}
                      {[
                        ...c.sugeridosDelGiro.faltanModulos.map((m) => m.label),
                        ...(c.sugeridosDelGiro.faltaApp ? [c.sugeridosDelGiro.app.label] : []),
                        ...(c.sugeridosDelGiro.faltaAsientos
                          ? [`usuarios: ${c.sugeridosDelGiro.asientos.completo} completos y ${c.sugeridosDelGiro.asientos.operativo} operativos`]
                          : []),
                      ].join(', ')}.
                      Sin ellos la empresa nace sin esas pantallas, o con puestos que no pueden entrar.
                    </p>
                    <button type="button" className="text-primary text-body-sm font-medium underline" onClick={c.aplicarSugeridosDelGiro}>
                      Añadir al paquete
                    </button>
                  </div>
                ) : (
                  <p className="text-caption text-neutral-600 dark:text-neutral-400">El paquete ya incluye lo que el rubro suele llevar.</p>
                )}
                {c.sugeridosDelGiro.puestos.length > 0 && (
                  <p className="text-caption text-neutral-500 dark:text-neutral-400">
                    Puestos que trae el rubro: {c.sugeridosDelGiro.puestos.join(', ')}.
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div id="identidad-fiscal" className="scroll-mt-24">
        <IdentidadFiscal
          emisor={form.emisor}
          onChange={(emisor) => setForm((f) => ({ ...f, emisor }))}
          cats={c.catFiscal}
          cargando={c.catFiscalCargando}
          pais={form.pais}
        />
      </div>
    </div>
  )
}
